<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1>
          User Management
          <v-btn variant="outlined" class="float-right" @click="refreshUsers" color="primary">
            Refresh
          </v-btn>
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="users"
          :items-per-page="30"
          class="elevation-1 wide-table"
        >
          <template v-slot:item.name="{ item }">
            <span class="v-align">
              <img :src="item.image_url" alt="User Image" class="user-image" referrerpolicy="no-referrer" />
              {{ item.name }}
            </span>
          </template>
          <template v-slot:item.aquarium_count="{ item }">
            <router-link :to="{ name: 'Aquarium Management', query: { user_id: item.id } }">
              {{ item.aquarium_count }}
            </router-link>
          </template>
          <template v-slot:item.log_count="{ item }">
            {{ item.log_count }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn @click="modalStore.openNewUserModal({ verb: 'edit', prefilledData: item })" color="primary" rounded="xl" size="x-small">Edit</v-btn>
            <v-btn @click="confirmDelete(item.id)" color="error" rounded="xl" size="x-small">Delete</v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-btn variant="outlined" class="float-right" color="primary" @click="modalStore.openNewUserModal({ verb: 'create', prefilledData: {} })">
          Create New
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="showDeleteModal" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Deletion</v-card-title>
        <v-card-text>Are you sure you want to delete this user?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="red darken-1" @click="deleteUser(confirmUserId)">Yes</v-btn>
          <v-btn class="green darken-1" @click="showDeleteModal = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from 'axios';
import { usePopupStore } from '@/store';
import { useModalStore } from '@/store';
import { useHead } from '@vueuse/head';

export default {
  name: 'UserManagement',
  setup() {
    useHead({
      title: 'Reeflendar.com - User Management',
      meta: [
        { name: 'description', content: 'Manage users in Reeflendar.' },
        { name: 'keywords', content: 'admin, user management, aquarium' },
      ],
    });
    return {
      popupStore: usePopupStore(),
      modalStore: useModalStore(),
    };
  },
  data() {
    return {
      users: [],
      showDeleteModal: false,
      confirmUserId: null,
      headers: [
        { title: 'ID', key: 'id' },
        { title: 'Name', key: 'name' },
        { title: 'Email', key: 'email' },
        { title: 'Subscription Level', key: 'subscription_level' },
        { title: 'Created At', key: 'created_at' },
        { title: 'Last Login', key: 'last_login_at' },
        { title: 'Aquarium Count', key: 'aquarium_count' },
        { title: 'Log Count', key: 'log_count' }, // <-- Added column
        { title: 'Actions', key: 'actions', sortable: false },
      ],
    };
  },
  methods: {
    async fetchUsers() {
      try {
        const userId = this.$route.query.id;
        const response = await axios.get('/api/users/');
        if (userId) {
          this.users = response.data.filter(user => user.id == userId);
        }
        else
        {
          this.users = response.data;
        }
      } catch (error) {
        this.popupStore.setMessage('Error Reading User', error);
      }
    },
    refreshUsers() {
      this.$router.push({ query: {} }).then(() => {
        this.fetchUsers();
      });
    },
    async createUser() {
      try {
        await axios.post('/api/users/', this.newUser);
        this.newUser.email = '';
        this.newUser.subscription_level = '';
        this.fetchUsers(); // Fetch users after successful creation
      } catch (error) {
        this.popupStore.setMessage("Error Creating User", error);
      }
    },
    confirmDelete(userId) {
      this.confirmUserId = userId;
      this.showDeleteModal = true;
    },
    async deleteUser(userId) {
      try {
        await axios.delete(`/api/users/${userId}`);
        this.users = this.users.filter(user => user.id !== userId);
        this.showDeleteModal = false;
      } catch (error) {
        this.popupStore.setMessage('Error Deleting User', error);
      }
    },
  },
  mounted() {
    this.fetchUsers();
    this.$eventBus.on('user-updated', this.fetchUsers);
  },
  beforeUnmount() {
    this.$eventBus.off('user-updated', this.fetchUsers);
  },
};
</script>

<style scoped>
.v-align {
  display: flex;
  align-items: center;
}

.user-image {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-right: 10px;
}

/* Make the data table take full width */
.wide-table {
  width: 100%;
  min-width: 1200px;
}
</style>